/**
 * Tokens de diseño — espejo en TypeScript de `src/styles/index.css`.
 *
 * ¿Por qué existe este archivo si Tailwind v4 ya lee los tokens desde el CSS?
 * Porque hay cosas que Tailwind no puede generar: series de gráficas, colores de
 * SVG inline, estilos calculados en runtime (ej. `style={{ color: colors.brand[600] }}`).
 * Aquí los tienes tipados y en un solo sitio.
 *
 * REGLA: si cambias un color aquí, cámbialo también en `src/styles/index.css`
 * (`npm run check:theme` falla si un color de este archivo no existe allá).
 *
 * Extraídos de la imagen de referencia (ver `frontend/design-reference/`).
 */

/** Escala de la marca (azul primario de la referencia). */
export const brand = {
  50: '#EFF5FF',
  100: '#E4EDFF',
  200: '#C6DAFF',
  300: '#93B4FD',
  400: '#5B8DEF',
  500: '#3B76F6',
  600: '#2563EB',
  700: '#1D4ED8',
  800: '#1B3FA8',
  900: '#16307A',
} as const;

/** Superficies, líneas y jerarquía de texto. */
export const surface = {
  canvas: '#F5F7FB',
  surface: '#FFFFFF',
  surfaceMuted: '#F2F4F7',
  line: '#EAECF0',
  lineStrong: '#D9DEE7',
  ink: '#101828',
  inkSoft: '#344054',
  inkMuted: '#667085',
  inkFaint: '#98A2B3',
} as const;

/** Estados (éxito / error / advertencia / info) — el color y su versión "suave" de fondo. */
export const status = {
  success: '#16A34A',
  successStrong: '#15803D',
  successSoft: '#E7F8EF',
  danger: '#DC2626',
  dangerStrong: '#B42318',
  dangerSoft: '#FEF3F2',
  warning: '#F59E0B',
  warningStrong: '#B45309',
  warningSoft: '#FEF6E7',
  info: '#2563EB',
  infoSoft: '#EFF5FF',
} as const;

/**
 * Paleta para gráficas (orden de asignación de series) y color de la grilla.
 * Orden tomado de la referencia: azul → violeta → verde → gris → azul claro → ámbar.
 */
export const chart = {
  series: ['#2563EB', '#8B5CF6', '#34D399', '#98A2B3', '#60A5FA', '#F59E0B'],
  grid: '#EEF1F6',
} as const;

/**
 * Cifras de layout compartidas por el shell. Tailwind usa valores equivalentes como
 * utilidades numéricas (`w-60` = 240px, `h-18` = 72px) porque el color/tamaño de estos
 * valores no se puede leer desde JS: aquí viven para cálculos (ej. offsets de un drawer).
 */
export const layout = {
  sidebarWidth: 240,
  headerHeight: 72,
  contentPadding: 24,
  cardPadding: 24,
  maxContentWidth: 1600,
} as const;

/** Radios en px, para SVG o estilos inline. */
export const radius = {
  control: 10,
  panel: 12,
  card: 16,
} as const;

/** Escala tipográfica (rem). */
export const fontSize = {
  micro: '0.75rem',
  caption: '0.8125rem',
  body: '0.875rem',
  cardTitle: '1rem',
  kpi: '1.75rem',
  page: '1.625rem',
  display: '2rem',
} as const;

export const fontFamily = {
  sans: "'Inter', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
} as const;

/** Todo el set de tokens en un solo objeto (útil para documentación/serialización). */
export const theme = {
  brand,
  surface,
  status,
  chart,
  layout,
  radius,
  fontSize,
  fontFamily,
} as const;

export type Theme = typeof theme;
